'use client';

import React from 'react';
import { faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { CardSpot } from './CardSpot';
import { useFilters } from '@/app/hooks/useFilters';
import { capitalizeFirstLetter } from '@/utils/lib/firstLetterCapitalize';

export const FilterMenu: React.FC = (): React.JSX.Element => {
  const {
    filters,
    handleFilterChange,
    allBrands,
    brandsCount,
    filteredModels,
    modelsCount,
    isLoading,
    spots,
    visibleCount,
    setVisibleCount,
  } = useFilters();

  return (
    <>
      <h2 id="filter-title" className="sr-only">Filtrer les spots</h2>
      <div className="relative inline-block w-full sm:w-auto mr-4">
        <label htmlFor="brands" className="sr-only">
          Toutes les marques
        </label>
        <select
          id="brands"
          className="min-w-40 w-full text-white bg-black border border-gray-700 p-2 rounded-lg appearance-none pr-8 hover:border-red-400 font-primary"
          value={filters.brand_name}
          onChange={(e) => handleFilterChange('brand_name', e.target.value)}
        >
          <option value="">Toutes les marques ({brandsCount})</option>
          {allBrands.map((brand, index) => (
            <option key={index} value={brand} className="font-primary">
              {capitalizeFirstLetter(brand)}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1">
          <span>🔻</span>
        </span>
      </div>

      <div className="relative inline-block sm:w-auto">
        <label htmlFor="models" className="sr-only">
          Tous les models ({modelsCount})
        </label>
        <select
          id="models"
          className="w-full text-white bg-black border border-gray-700 p-2 rounded-lg appearance-none pr-8 font-primary"
          value={filters.model}
          onChange={(e) => handleFilterChange('model', e.target.value)}
          disabled={!filters.brand_name}
        >
          <option value="">
            {filters.brand_name
              ? `Tous les models (${modelsCount})`
              : `Sélectionne un model`}
          </option>
          {filteredModels.map((model, index) => (
            <option key={index} value={model}>
              {capitalizeFirstLetter(model)}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1">
          <span>🔻</span>
        </span>
      </div>

      <label htmlFor="search" className="sr-only">
        Rechercher
      </label>
      <input
        id="search"
        type="search"
        placeholder="🔍 Porsche 911, Audi A1..."
        className="w-full text-white bg-zinc-800 border p-2 rounded-lg focus:outline-none font-primary"
        value={filters.search_query}
        onChange={(e) => handleFilterChange('search_query', e.target.value)}
      />

      <div className="mt-4">
        <span className="block text-sm font-medium text-gray-400 font-primary">
          Score :
        </span>
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <label htmlFor="min-rating" className="sr-only">
              Note minimale
            </label>
            <input
              id="min-rating"
              type="range"
              min={0}
              max={100}
              value={filters.min_rating}
              onChange={(e) =>
                handleFilterChange('min_rating', parseInt(e.target.value, 10))
              }
              className="w-full appearance-none h-2 rounded-lg"
            />
            <p className="text-sm text-gray-400 mt-1 font-primary">
              Min: {filters.min_rating}
            </p>
          </div>
          <div className="flex-1">
            <label htmlFor="max-rating" className="sr-only">
              Note maximale
            </label>
            <input
              id="max-rating"
              type="range"
              min={0}
              max={100}
              value={filters.max_rating}
              onChange={(e) =>
                handleFilterChange('max_rating', parseInt(e.target.value, 10))
              }
              className="w-full appearance-none h-2 rounded-lg"
            />
            <p className="text-sm text-gray-400 mt-1 font-primary">
              Max: {filters.max_rating}
            </p>
          </div>
        </div>
      </div>

      <div>
        {isLoading ? (
          <p>Chargement des spots...</p>
        ) : spots.length > 0 ? (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {spots
              .filter(
                (spot) =>
                  !filters.model ||
                  spot.model.toLowerCase() === filters.model.toLowerCase()
              )
              .slice(0, visibleCount)
              .map((spot) => (
                <li
                  key={spot.id}
                  aria-labelledby={
                    'spot-title-' + spot.brand_name && spot.model
                      ? capitalizeFirstLetter(spot.brand_name) +
                        '  ' +
                        capitalizeFirstLetter(spot.model)
                      : 'N/a'
                  }
                >
                  <CardSpot
                    spotId={spot.id}
                    imageSrc={spot.photo}
                    titleSpot={
                      spot.brand_name && spot.model
                        ? capitalizeFirstLetter(spot.brand_name) +
                          '  ' +
                          capitalizeFirstLetter(spot.model)
                        : 'N/a'
                    }
                    iconLocation={faLocationDot}
                    location={spot.geo_description}
                    aliasUser={spot.alias}
                    rating={spot.rating}
                  />
                </li>
              ))}
          </ul>
        ) : (
          <p className="font-primary">Aucun spot trouvé.</p>
        )}

        <div className="text-center">
          {visibleCount < spots.length && (
            <button
              onClick={() => setVisibleCount((prev) => prev + 9)}
              className="mt-4 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-800 font-primary"
            >
              Voir Plus
            </button>
          )}
        </div>
      </div>
    </>
  );
};